import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Slider,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { fetchBatteryMetrics } from '../../../services/batteryMetricsService';
import { processPacketData } from '../../../utils/batteryDataProcessor';

const batteryMetrics = [
  { value: 'voltage', label: 'Battery Voltage' },
  { value: 'current', label: 'Battery Current' },
  { value: 'temperature', label: 'Battery Temperature' },
  { value: 'soc', label: 'State of Charge' },
  { value: 'health', label: 'Battery Health' }
];

const yAxisConfig = {
  voltage: { 
    label: 'Voltage (V)', 
    domain: ['auto', 'auto'],
    margin: 5
  },
  current: { label: 'Current (A)', domain: ['auto', 'auto'] },
  temperature: { label: 'Temperature (°C)', domain: [0, 60] },
  soc: { label: 'SOC (%)', domain: [0, 100] },
  health: { label: 'Health (%)', domain: [0, 100] }
};

const timeFilters = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const WINDOW_SIZES = {
  '1hour': 60 * 60 * 1000,
  '2hour': 2 * 60 * 60 * 1000,
  '3hour': 3 * 60 * 60 * 1000,
  '4hour': 4 * 60 * 60 * 1000,
  '5hour': 5 * 60 * 60 * 1000,
};

const menuItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  '& .MuiMenuItem-root': {
    borderBottom: '1px solid #eee',
    py: 1,  // Reduced vertical padding
    px: 0.5,  // Minimal horizontal padding
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  '& .MuiList-root': {
    padding: '2px 0',  // Reduced padding for the menu list container
  },
  '& .MuiPaper-root': {
    minWidth: 'auto'  // Allow paper to shrink to content
  }
};

const timeRangeMenuItemStyle = {
  my: 0.5,  // Add vertical margin between items
  minHeight: '32px',  // Reduced height
  display: 'flex',
  alignItems: 'center',
  px: 0.5,  // Reduced horizontal padding
  fontSize: '1rem'  // Default font size
};

const BatteryMetrics = ({ emsName }) => {
  const [selectedMetric, setSelectedMetric] = useState('voltage');
  const [timeFilter, setTimeFilter] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [windowSize, setWindowSize] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(0);

  const fetchData = useCallback(async () => {
    if (!emsName) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await fetchBatteryMetrics(emsName, timeFilter, selectedDate);
      if (result && result.data) {
        setData(Array.isArray(result.data) ? result.data : []);
      } else {
        setData([]);
        setError('No data received');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load battery metrics');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [emsName, timeFilter, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processedData = useMemo(() => {
    return processPacketData(data, timeFilter) || [];
  }, [data, timeFilter]);

  const visibleData = useMemo(() => {
    if (!processedData.length) return [];
    if (!windowSize) return processedData;
    
    try {
      // Debug raw data
      console.log('Raw processed data:', processedData[0]);

      // Convert timestamps to Date objects with validation
      const dataWithDates = processedData
        .map(item => {
          try {
            const timestamp = item.timestamp;
            let dateTime = null;

            if (typeof timestamp === 'string' && timestamp.includes('T')) {
              // ISO string format
              dateTime = new Date(timestamp);
            } else if (typeof timestamp === 'number' || !isNaN(parseInt(timestamp))) {
              // Unix timestamp (milliseconds)
              dateTime = new Date(parseInt(timestamp));
            }

            if (dateTime && !isNaN(dateTime.getTime())) {
              return {
                ...item,
                dateTime,
                originalTime: item.time
              };
            }
            return null;
          } catch (err) {
            console.error('Error parsing date for item:', item);
            return null;
          }
        })
        .filter(item => item !== null); // Remove items with invalid dates

      if (dataWithDates.length === 0) {
        console.error('No valid dates found in data. Sample data:', {
          first: processedData[0],
          total: processedData.length
        });
        return processedData;
      }

      // Sort by timestamp in ascending order
      const sortedData = [...dataWithDates].sort((a, b) => 
        a.dateTime.getTime() - b.dateTime.getTime()
      );

      // Calculate window indices
      const totalPoints = sortedData.length;
      const windowMs = WINDOW_SIZES[windowSize];
      const pointsPerWindow = Math.ceil(totalPoints * (windowMs / (24 * 60 * 60 * 1000)));
      const availablePositions = Math.max(0, totalPoints - pointsPerWindow);
      const startIndex = Math.floor(availablePositions * (sliderPosition / 100));
      const endIndex = Math.min(startIndex + pointsPerWindow, totalPoints);

      console.log('Window calculation:', {
        totalPoints,
        pointsPerWindow,
        startIndex,
        endIndex,
        windowMs,
        firstDate: sortedData[0]?.dateTime,
        lastDate: sortedData[sortedData.length - 1]?.dateTime
      });

      return sortedData
        .slice(startIndex, endIndex)
        .map(({ dateTime, ...rest }) => ({
          ...rest,
          time: dateTime.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
        }));

    } catch (error) {
      console.error('Error in visibleData calculation:', error);
      return processedData;
    }
  }, [processedData, windowSize, sliderPosition]);

  const handleMetricChange = useCallback((event) => {
    setSelectedMetric(event.target.value);
  }, []);

  const handleTimeFilterChange = useCallback((event) => {
    setTimeFilter(event.target.value);
    // Reset window size when changing to weekly or monthly
    if (event.target.value !== 'daily') {
      setWindowSize(null);
    }
  }, []);

  const handleDateChange = useCallback((newDate) => {
    if (newDate && !isNaN(newDate.getTime())) {
      setSelectedDate(prevDate => {
        // Only update if the date is actually different
        return newDate.getTime() !== prevDate.getTime() ? newDate : prevDate;
      });
    }
  }, []);

  const handleWindowChange = useCallback((size) => {
    setWindowSize(size);
    setSliderPosition(0);
    // Don't change timeFilter, keep it as 'daily'
  }, []);

  const handleSliderChange = (event, newValue) => {
    setSliderPosition(newValue);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ minWidth: 200 }}>
      {/* Menu Controls Box */}
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        justifyContent: 'flex-start', // Align to the left
        '& .MuiFormControl-root': {
          minWidth: 180  // Slightly reduced from 200 for better fit
        }
      }}>
        <FormControl size="small">
          <InputLabel>Battery Metric</InputLabel>
          <Select
            value={selectedMetric}
            label="Battery Metric"
            onChange={handleMetricChange}
            MenuProps={{
              PaperProps: {
                sx: menuItemStyle
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
          >
            {batteryMetrics.map((metric) => (
              <MenuItem 
                key={metric.value} 
                value={metric.value}
                sx={{
                  minHeight: '32px',  // Reduced height
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,  // Reduced horizontal padding
                  fontSize: '1rem'  // Default font size
                }}
              >
                {metric.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeFilter}
            label="Time Range"
            onChange={handleTimeFilterChange}
            MenuProps={{
              PaperProps: {
                sx: menuItemStyle
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
          >
            {timeFilters.map((filter) => (
              <MenuItem 
                key={filter.value} 
                value={filter.value}
                sx={timeRangeMenuItemStyle}
              >
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              maxDate={new Date()}
              views={['year', 'month', 'day']}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  inputProps: {
                    readOnly: true
                  }
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Box>

      {/* Chart Area */}
      <Box sx={{ mt: 2, mb: 3 }}>
        {processedData.length > 0 ? (
          <>
            {timeFilter === 'daily' && (
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                gap: 1,
                justifyContent: 'center', // Center align horizontally
                width: '100%' // Ensure full width
              }}>
                <Button
                  variant={!windowSize ? 'contained' : 'outlined'}
                  onClick={() => handleWindowChange(null)}
                  size="small"
                >
                  View Full Day
                </Button>
                {Object.keys(WINDOW_SIZES).map((size) => (
                  <Button
                    key={size}
                    variant={windowSize === size ? 'contained' : 'outlined'}
                    onClick={() => handleWindowChange(size)}
                    size="small"
                    sx={{ minWidth: '80px' }}
                  >
                    {`${size.replace('hour', '')}h Window`}
                  </Button>
                ))}
              </Box>
            )}
            
            <Box sx={{ 
              position: 'relative', 
              overflowX: 'auto',
              maxWidth: '100%' 
            }}>
              <LineChart 
                width={900} // Increased from 600 to 900
                height={300} 
                data={visibleData}
                margin={{ top: 5, right: 30, left: 50, bottom: 25 }} // Increased bottom margin
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={timeFilter === 'monthly' ? Math.ceil(visibleData.length / 31) : 
                           timeFilter === 'weekly' ? Math.ceil(visibleData.length / 7) - 1 : 
                           'preserveStart'}
                  tickFormatter={(value) => {
                    if (timeFilter === 'weekly') {
                      return value.split(',')[0]; // Show only day name for weekly view
                    }
                    return value;
                  }}
                />
                <YAxis 
                  label={{ 
                    value: yAxisConfig[selectedMetric].label, 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: 0,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666'
                    },
                    dx: -35
                  }}
                  domain={yAxisConfig[selectedMetric].domain}
                  width={80}
                  tickMargin={5}
                  tickFormatter={(value) => 
                    selectedMetric === 'voltage' ? value.toFixed(2) : value
                  }
                />
                <Tooltip
                  labelFormatter={(label, items) => {
                    const item = items[0]?.payload;
                    if (timeFilter === 'monthly' && item?.dataPoints) {
                      return `${label} (${item.dataPoints} measurements)`;
                    }
                    return item?.tooltipTime || label;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={selectedMetric} 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                />
              </LineChart>
            </Box>

            {/* Only show slider for daily view with window size */}
            {timeFilter === 'daily' && windowSize && processedData.length > 0 && (
              <Box sx={{ width: 900, mt: 2, px: 2 }}> {/* Increased from 600 to 900 */}
                <Typography variant="caption" color="textSecondary">
                  Slide to navigate through data
                </Typography>
                <Slider
                  value={sliderPosition}
                  onChange={handleSliderChange}
                  aria-label="Time window slider"
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  step={1}
                />
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">No data available for the selected period</Alert>
        )}
      </Box>
    </Box>
  );
};

// Wrap with memo and provide comparison function
export default React.memo(BatteryMetrics, (prevProps, nextProps) => {
  return prevProps.emsName === nextProps.emsName;
});